import { message } from 'antd';
import { HttpStatusCode } from './constant';
import { buildURL, buildFetchInit, parseResponseData } from './util';
import { sgmReport } from '@/utils/util';
/**
 * @param {RequestOptions} param 接收参数
 * @param {string} [param.path] 请求地址
 * @param {boolean} [autoTip=true] 是否自动提示
 * @return {Promise}
 */

export const doRequest = async (param: RequestOptions, autoTip = true) => {
  if (param.useMock) {
    return {
      code: HttpStatusCode.Success,
      data: param.mockData,
    };
  }
  const url = buildURL(param);
  const fetchInit = buildFetchInit(param);
  try {
    const res = await window.fetch(url, fetchInit);
    const respBody = await parseResponseData(
      res,
      res.headers.get('Content-Type')
    );
    const httpCode: any = res.status;
    const resCode: any = respBody.code;
    const _CodeMap: any = {
      401: {
        code: 401,
        message: '登录过期，请重新登录',
      },
      403: {
        code: 403,
        message: '无权限',
      },
      200: {
        code: HttpStatusCode.Success,
        ...respBody,
      },
    };
    if (_CodeMap[httpCode || respBody]) {
      if (httpCode === 401 || resCode === 401) {
        if (location.pathname !== '/login') {
          location.replace('/login');
        }
        return _CodeMap[httpCode || respBody];
      }
      if (respBody?.error_response) {
        return {
          code: HttpStatusCode.ServiceException,
          message: respBody?.error_response?.zh_desc,
        };
      }
      return _CodeMap[httpCode || respBody];
    } else {
      return {
        code: `${httpCode}`,
        message: '网络错误',
      };
    }
  } catch (e: any) {
    if (e.name === 'AbortError') {
      sgmReport(`FETCH_ABORTED/${param.path}`, '');
      return { code: 'FETCH_ABORTED', message: 'Request was aborted' };
    }
    return { code: 'NETWORK_ERROR', message: `Network error: ${e.message}` };
  }
};

const defaultRequestOption: RequestOptions = {
  method: 'POST',
  contentType: 'application/json',
  timeout: 15000,
};

function makeTimeoutPromise(timeout: number) {
  return new Promise((resolve: any, reject: any) => {
    setTimeout(() => {
      resolve({
        code: '3001',
        message: '请求超时',
        requestId: '',
        eventType: '',
        traceId: '',
      });
    }, timeout);
  });
}

/**
 *
 * @param {RequestOptions} opts 请求参数
 * @return {Promise}
 */
export async function request(opts: RequestOptions) {
  const options = Object.assign({}, defaultRequestOption, opts);
  return Promise.race([
    makeTimeoutPromise(options.timeout!),
    doRequest(options),
  ]);
}

export function cancellableRequest<T>(opts: RequestOptions): {
  promise: Promise<T>;
  cancel: () => void;
} {
  const controller = new AbortController();

  const options = Object.assign({}, defaultRequestOption, opts, {
    signal: controller.signal,
  });

  let timeoutId: NodeJS.Timeout | null = null;
  let timedOut = false;

  const promise = new Promise((resolve, reject) => {
    if (options.timeout) {
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, options.timeout);
    }

    doRequest(options)
      .then((response: any) => {
        if (timeoutId) clearTimeout(timeoutId);
        if (response.code === 'FETCH_ABORTED' && timedOut) {
          resolve({
            code: '3001',
            message: '请求超时',
            requestId: '',
            eventType: '',
            traceId: '',
          } as any);
        } else {
          resolve(response);
        }
      })
      .catch((error: any) => {
        if (timeoutId) clearTimeout(timeoutId);
        reject(error);
      });
  }) as Promise<T>;

  return {
    promise,
    cancel: () => controller.abort(),
  };
}
